import React from 'react';
import { Button, Chip, colors } from '@dhis2/ui';
import { Printer, FileDown, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ReportLayout from '../reports/ReportLayout';

const AppraisalReport = ({ 
  appraisalData, 
  reportType, 
  onClose,
  isTopLevelUser = false,  
  isPrintMode = false, 
}) => {
  const navigate = useNavigate();
  
  if (!appraisalData) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <p>No appraisal data available</p>
      </div>
    );
  }

  // ✅ FIX: Properly destructure appraisalData
  const {
    data,
    recentObjectives = [],
    competencyRatings = {},
    leadershipCompetencies = [],
    businessRatingScale = {},
    leadershipRatingScale = {},
    employeeName = 'Employee',
    positionTitle = 'Position',
    fiscalYear = {},
  } = appraisalData;

  const isMidYear = reportType === 'mid-year';

  // Calculate total scores
  const employeeTaskRating = parseFloat(data?.employeeSelfRatingTask) || 0;
  const employeeBehaviouralRating = parseFloat(data?.employeeSelfRatingBehavioural) || 0;

  return (
    <ReportLayout
      badge={isMidYear ? 'Mid-Year Appraisal' : 'Year-End Appraisal'}
      title={isMidYear ? 'Mid-Year Performance Appraisal' : 'Year-End Performance Appraisal'}
      description="Review your appraisal and print the current appraisal form as a PDF Layout."
      hideAutoApproved={true}
      hideSelfRating={true}
      isTopLevelUser={isTopLevelUser}  
      isPrintMode={isPrintMode} 
    >
      <div>
        {/* ============================================ */}
        {/* Employee Info - Clean header */}
        {/* ============================================ */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '20px',
          padding: '12px 16px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          border: '1px solid #e0e0e0'
        }}>
          <div>
            <h4 style={{ margin: 0, fontSize: '1.1rem' }}>{employeeName}</h4>
            <p style={{ margin: '4px 0 0', color: '#666', fontSize: '0.85rem' }}>{positionTitle}</p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <p style={{ margin: 0, fontSize: '0.85rem', color: '#666' }}>
              {fiscalYear?.start} - {fiscalYear?.end}
            </p>
          </div>
        </div>

        {/* ============================================ */}
        {/* Objectives Section - Clean, minimal */}
        {/* ============================================ */}
        {recentObjectives.length > 0 && (
          <div style={{ marginBottom: '30px' }}>
            <h3 style={{ 
              borderBottom: '2px solid #0f5132', 
              paddingBottom: '8px',
              marginBottom: '16px',
              fontSize: '1.1rem'
            }}>
              Total Objectives ({recentObjectives.length})
            </h3>
            {recentObjectives.map((obj, index) => (
              <div key={obj.id || index} style={{
                padding: '12px 16px',
                marginBottom: '12px',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                backgroundColor: '#fff',
                pageBreakInside: 'avoid'
              }}>
                <h4 style={{ margin: '0 0 4px 0', fontSize: '1rem' }}>{obj.name}</h4>
                {obj.description && (
                  <p style={{ margin: '0 0 8px 0', color: '#666', fontSize: '0.85rem' }}>
                    {obj.description}
                  </p>
                )}
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', fontSize: '0.85rem' }}>
                  <div>
                    <span style={{ fontWeight: 600 }}>Self Rating: </span>
                    <span>{obj.dataValues?.find(dv => dv.dataElement === 'selfRating')?.value || obj.selfRating || '—'}</span>
                  </div>
                  <div>
                    <span style={{ fontWeight: 600 }}>Progress: </span>
                    <span>{obj.progress || 0}%</span>
                  </div>
                  <div>
                    <span style={{ fontWeight: 600 }}>Due: </span>
                    <span>{obj.completionDate || '—'}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ============================================ */}
        {/* Overall Self-Evaluation - Clean summary */}
        {/* ============================================ */}
        <div style={{ marginBottom: '30px' }}>
          <h3 style={{ 
            borderBottom: '2px solid #0f5132', 
            paddingBottom: '8px',
            marginBottom: '16px',
            fontSize: '1.1rem'
          }}>
            Overall Self-Evaluation
          </h3>
          
          {/* Business Objectives Summary */}
          <div style={{ 
            padding: '16px', 
            backgroundColor: '#f8f9fa', 
            borderRadius: '8px',
            marginBottom: '12px',
            border: '1px solid #e0e0e0'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
              <div>
                <h4 style={{ margin: 0, fontSize: '0.95rem' }}>Business Performance Objectives</h4>
                <p style={{ margin: '2px 0 0', color: '#666', fontSize: '0.8rem' }}>
                  Weight: 50%
                </p>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '0.85rem' }}>
                  <span style={{ fontWeight: 600 }}>Self Rating: </span>
                  <span style={{ fontWeight: 700, fontSize: '1rem' }}>{data?.employeeSelfRatingTask || '—'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Leadership Competencies Summary */}
          <div style={{ 
            padding: '16px', 
            backgroundColor: '#f8f9fa', 
            borderRadius: '8px',
            border: '1px solid #e0e0e0'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
              <div>
                <h4 style={{ margin: 0, fontSize: '0.95rem' }}>Leadership & Behavioural Competencies</h4>
                <p style={{ margin: '2px 0 0', color: '#666', fontSize: '0.8rem' }}>
                  Weight: 50%
                </p>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '0.85rem' }}>
                  <span style={{ fontWeight: 600 }}>Self Rating: </span>
                  <span style={{ fontWeight: 700, fontSize: '1rem' }}>{data?.employeeSelfRatingBehavioural || '—'}</span>
                </div>
              </div>
            </div>
            
            {/* Individual Competency Ratings - Clean list */}
            <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e0e0e0' }}>
              {leadershipCompetencies.map((comp) => (
                <div key={comp.id} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '4px 0',
                  fontSize: '0.85rem',
                  borderBottom: '1px solid #f0f0f0'
                }}>
                  <span>{comp.name} ({Math.round(comp.weight * 100)}%)</span>
                  <span style={{ fontWeight: 600 }}>{competencyRatings[comp.id] || '—'}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Self-Assessment Comments */}
          {data?.employeeSelfComments && (
            <div style={{ 
              marginTop: '16px', 
              padding: '16px', 
              backgroundColor: '#fff', 
              borderRadius: '8px', 
              border: '1px solid #e0e0e0'
            }}>
              <h4 style={{ margin: 0, fontSize: '0.95rem' }}>Self-Assessment Comments</h4>
              <p style={{ margin: '8px 0 0', fontStyle: 'italic', fontSize: '0.9rem' }}>"{data.employeeSelfComments}"</p>
            </div>
          )}
        </div>

        {/* ============================================ */}
        {/* Per-Objective Manual Review Placeholders */}
        {/* ============================================ */}
        {recentObjectives.length > 0 && (
          <div style={{ marginBottom: '30px' }}>
            <h3 style={{ 
              borderBottom: '2px solid #0f5132', 
              paddingBottom: '8px',
              marginBottom: '16px',
              fontSize: '1.1rem'
            }}>
              Manager's Review 
            </h3>
            
            {recentObjectives.map((obj, index) => (
              <div key={obj.id || index} style={{
                padding: '16px',
                marginBottom: '16px',
                border: '1px solid #ccc',
                borderRadius: '8px',
                backgroundColor: '#fafafa',
                pageBreakInside: 'avoid'
              }}>
                <h5 style={{ margin: '0 0 8px 0', fontSize: '0.95rem', color: '#0f5132' }}>
                  {obj.name}
                </h5>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: '1fr 1fr', 
                  gap: '16px',
                  marginBottom: '12px'
                }}>
                  <div>
                    <span style={{ fontSize: '0.75rem', color: '#666', fontWeight: 600, display: 'block', marginBottom: '4px' }}>
                      Manager Rating (0-5):
                    </span>
                    <div style={{ 
                      borderBottom: '2px solid #0f5132', 
                      minHeight: '28px', 
                      width: '100%',
                      backgroundColor: '#fff',
                      borderRadius: '4px',
                      padding: '2px 8px'
                    }}>
                      <span style={{ color: '#999', fontSize: '0.8rem' }}>_____</span>
                    </div>
                  </div>
                  <div>
                    <span style={{ fontSize: '0.75rem', color: '#666', fontWeight: 600, display: 'block', marginBottom: '4px' }}>
                      Status:
                    </span>
                    <div style={{ 
                      borderBottom: '2px solid #0f5132', 
                      minHeight: '28px', 
                      width: '100%',
                      backgroundColor: '#fff',
                      borderRadius: '4px',
                      padding: '2px 8px'
                    }}>
                      <span style={{ color: '#999', fontSize: '0.8rem' }}>Approved / Rejected / Pending</span>
                    </div>
                  </div>
                </div>
                <div>
                  <span style={{ fontSize: '0.75rem', color: '#666', fontWeight: 600, display: 'block', marginBottom: '4px' }}>
                    Manager Comments:
                  </span>
                  <div style={{ 
                    borderBottom: '2px solid #ccc', 
                    minHeight: '40px', 
                    width: '100%',
                    backgroundColor: '#fff',
                    borderRadius: '4px',
                    padding: '8px'
                  }}>
                    <span style={{ color: '#bbb', fontSize: '0.75rem' }}>Write your feedback here...</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ============================================ */}
        {/* Per-Competency Manual Review Placeholders */}
        {/* ============================================ */}
        {leadershipCompetencies.length > 0 && (
          <div style={{ marginBottom: '30px' }}>
            <h3 style={{ 
              borderBottom: '2px solid #0f5132', 
              paddingBottom: '8px',
              marginBottom: '16px',
              fontSize: '1.1rem'
            }}>
              Behavioural Competency Review 
            </h3>
            
            {leadershipCompetencies.map((comp) => (
              <div key={comp.id} style={{
                padding: '12px 16px',
                marginBottom: '12px',
                border: '1px solid #ccc',
                borderRadius: '8px',
                backgroundColor: '#fafafa',
                pageBreakInside: 'avoid'
              }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginBottom: '8px'
                }}>
                  <h5 style={{ margin: 0, fontSize: '0.9rem', color: '#0f5132' }}>
                    {comp.name} ({Math.round(comp.weight * 100)}%)
                  </h5>
                  <span style={{ fontSize: '0.8rem', color: '#666' }}>
                    Self Rating: {competencyRatings[comp.id] || '—'}
                  </span>
                </div>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: '1fr 1fr', 
                  gap: '12px'
                }}>
                  <div>
                    <span style={{ fontSize: '0.7rem', color: '#666', fontWeight: 600, display: 'block', marginBottom: '3px' }}>
                      Manager Rating (0-5):
                    </span>
                    <div style={{ 
                      borderBottom: '2px solid #0f5132', 
                      minHeight: '24px', 
                      width: '100%',
                      backgroundColor: '#fff',
                      borderRadius: '4px',
                      padding: '2px 8px'
                    }}>
                      <span style={{ color: '#999', fontSize: '0.75rem' }}>_____</span>
                    </div>
                  </div>
                  <div>
                    <span style={{ fontSize: '0.7rem', color: '#666', fontWeight: 600, display: 'block', marginBottom: '3px' }}>
                     Overall Manager Comments:
                    </span>
                    <div style={{ 
                      borderBottom: '2px solid #ccc', 
                      minHeight: '24px', 
                      width: '100%',
                      backgroundColor: '#fff',
                      borderRadius: '4px',
                      padding: '2px 8px'
                    }}>
                      <span style={{ color: '#bbb', fontSize: '0.7rem' }}>Write feedback...</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </ReportLayout>
  );
};

export default AppraisalReport;